import InfoIcon from '@mui/icons-material/Info';
import { useCallback, useMemo } from 'react';
import {
  AutocompleteInput,
  ChipField,
  Create,
  Edit,
  FormDataConsumer,
  Labeled,
  ReferenceField,
  ReferenceInput,
  SaveButton,
  SelectInput,
  SimpleForm,
  Toolbar,
  required,
  useGetIdentity,
  useGetList,
  useRecordContext,
} from 'react-admin';
import { useLogEffect } from '@/util/useLogEffect';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import { BSON } from 'realm-web';
import { TagInput } from '@/components/TagInput';
import {
  outputTypesChoices,
  outputTypesChoicesForCustomSessionNotes,
} from './AIPromptsList';

export const entityTypes = {
  therapistSessionNotes: 'Therapist - Session Notes',
  therapistLiveNotes: 'Therapist - Live Notes',
};

export const entityTypesChoices = Object.entries(entityTypes).map(
  ([id, name]) => ({
    id,
    name,
  }),
);

export const AIPromptsEdit = () => {
  const userId = useGetIdentity().data?.id?.toString();

  const transform = useCallback(
    (data: any) => {
      console.log('data', data);

      return {
        ...data,
        subType: data.subType === 'GENERAL' ? null : data.subType,
        updatedBy: userId,
        updatedAt: new Date(),
      };
    },
    [userId],
  );

  return (
    <Edit
      title={<EditTitle />}
      mutationMode="pessimistic"
      actions={false}
      transform={transform}
    >
      <AIPromptsEditForm />
    </Edit>
  );
};

export const AIPromptsCreate = () => {
  const userId = useGetIdentity().data?.id?.toString();

  const transform = useCallback(
    (data: any) => {
      console.log('data', data);

      return {
        ...data,
        subType: data.subType === 'GENERAL' ? null : data.subType,
        id: new BSON.ObjectId().toHexString(),
        // entityType: "therapistSessionNotes",
        createdBy: userId,
        createdAt: new Date(),
      };
    },
    [userId],
  );

  return (
    <Create
      redirect="list"
      transform={transform}
      title={<EditTitle />}
    >
      <AIPromptsEditForm />
    </Create>
  );
};

export const EditToolbar = () => (
  <Toolbar>
    <SaveButton label="Save" />
  </Toolbar>
);

export const EditTitle = () => {
  const record = useRecordContext();

  return <span>Agent Prompt: {record ? `${record.id}` : ''}</span>;
};

export const AIPromptsEditForm = () => {
  const record = useRecordContext();

  useLogEffect('record', record);

  const { data: aiPromptTags } = useGetList('aiPromptTags', {
    filter: {
      assetType: record?.entityType,
    },
  });

  const { data: frameworks } = useGetList(
    'therapistSessionNotesFrameworks',
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: 'name', order: 'ASC' },
    },
  );

  const frameworkChoices = useMemo(() => {
    const list =
      frameworks?.map((f: any) => ({
        id: f.id,
        name: f.name,
      })) || [];

    return [
      { id: 'GENERAL', name: 'General' },
      { id: 'custom', name: 'Custom' },
      ...list,
    ];
  }, [frameworks]);

  // Add debugging
  useLogEffect('aiPromptTags raw data', aiPromptTags);

  /** Runtime-injected tags for therapist assets: resolved when prompts run. */
  const RUNTIME_CLIENT_NAME_TAG = '@client.name';
  const RUNTIME_CLIENT_PRONOUNS_TAG = '@client.pronouns';

  const availableTags = useMemo(() => {
    const tags = aiPromptTags?.map((tag: any) => tag.tagName) ?? [];
    const entityType = record?.entityType as string | undefined;
    const isTherapistAsset =
      entityType === 'therapistSessionNotes' ||
      entityType === 'therapistLiveNotes' ||
      entityType === 'therapistHomework';
    if (!isTherapistAsset) {
      return tags;
    }
    const runtimeTags = [
      ...(tags.includes(RUNTIME_CLIENT_NAME_TAG) ? [] : [RUNTIME_CLIENT_NAME_TAG]),
      ...(tags.includes(RUNTIME_CLIENT_PRONOUNS_TAG)
        ? []
        : [RUNTIME_CLIENT_PRONOUNS_TAG]),
    ];
    return runtimeTags.length ? [...tags, ...runtimeTags] : tags;
  }, [aiPromptTags, record?.entityType]);

  // Add debugging
  useLogEffect('availableTags final', availableTags);

  return (
    <SimpleForm record={record} toolbar={<EditToolbar />}>
      {!record && (
        <Box>
          <SelectInput
            fullWidth
            label="Entity Type"
            source="entityType"
            choices={entityTypesChoices}
            validate={[required()]}
          />
        </Box>
      )}
      <Box display="flex" marginBottom="1rem" alignItems="flex-end">
        {!!record && (
          <>
            <Labeled label="Entity Type">
              <Box display="flex" alignItems="center">
                <Chip
                  label={
                    (entityTypes as any)[
                      (record as any)?.entityType ||
                        'therapistSessionNotes'
                    ]
                  }
                />
                {!record && (
                  <Box marginLeft="1rem">
                    <Tooltip title="Creating Agent Prompts is only supported for overriding therapist session notes.">
                      <InfoIcon />
                    </Tooltip>
                  </Box>
                )}
              </Box>
            </Labeled>
          </>
        )}

        {record?.entityType?.startsWith('therapist') && (
          <Box marginLeft="1rem">
            <Labeled label="Framework">
              {(record as { subType?: string | null }).subType ===
              'custom' ? (
                <Chip label="Custom" />
              ) : (
                <ReferenceField
                  source="subType"
                  record={record}
                  reference="therapistSessionNotesFrameworks"
                  emptyText="General"
                >
                  <ChipField source="name" />
                </ReferenceField>
              )}
            </Labeled>
          </Box>
        )}

        {!!record && (
          <Box marginLeft="1rem">
            <Labeled label="Output Type">
              <Chip label={record.outputType} />
            </Labeled>
          </Box>
        )}
      </Box>
      {!record && (
        <SelectInput
          fullWidth
          label="Framework"
          source="subType"
          choices={frameworkChoices}
          validate={[required()]}
          format={v => (v === null || v === '' ? 'GENERAL' : v)}
        />
      )}
      {!record && (
        <FormDataConsumer>
          {({ formData }) => (
            <SelectInput
              label="Output Type"
              source="outputType"
              fullWidth
              choices={
                formData?.subType === 'custom'
                  ? outputTypesChoicesForCustomSessionNotes
                  : outputTypesChoices
              }
              validate={[required()]}
            />
          )}
        </FormDataConsumer>
      )}
      <TagInput
        label="Instruction (Modern)"
        source="instruction"
        validTags={availableTags}
      />
      <Box marginTop="2rem" />
      <Box sx={{ opacity: 0.6 }}>
        <Typography variant="caption" color="warning.main">
          DEPRECATED FIELDS (Legacy structure)
        </Typography>
        <TagInput
          label="System Message (DEPRECATED)"
          source="systemMessage"
          validTags={availableTags}
          fullWidth
        />
        <TagInput
          label="Prompt Prepend (DEPRECATED)"
          source="promptPrepend"
          validTags={availableTags}
          fullWidth
        />
        <TagInput
          label="Prompt Append (DEPRECATED)"
          source="promptAppend"
          validTags={availableTags}
          fullWidth
        />
      </Box>
    </SimpleForm>
  );
};
